import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, BadgePercent, FileText, Handshake, Plus, Repeat, Undo2 } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, type Paginated } from '../lib/api';
import { P, useAuth } from '../lib/auth';
import { enumLabel, useI18n } from '../lib/i18n';
import { formatDate, formatMoney } from '../lib/format';
import { useCurrencies } from './Clients';
import { DownloadButton } from '../components/DownloadButton';
import { ReceiptLink } from '../components/ReceiptPicker';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Checkbox,
  Detail,
  EmptyState,
  ErrorState,
  Input,
  LoadingBlock,
  Metric,
  PageHeader,
  Pagination,
  ProgressBar,
  Select,
  Table,
  Td,
  Th,
  Tr,
  statusTone,
  useToast,
} from '../components/ui';

/**
 * CR-001 §10–§17, §46, §47 — a client receivable invoice.
 *
 * The form follows the §46 order: client first, then the project (whose picker
 * only loads once a client is chosen, §14), then the dates, the currency —
 * pre-filled from the client, §15 — and finally the line items.
 */

interface ClientOption {
  id: string;
  code: string;
  companyName: string;
  companyNameAr: string | null;
  defaultCurrency: string;
}

function useClientOptions() {
  return useQuery({
    queryKey: ['client-options'],
    queryFn: () => api.get<ClientOption[]>('/clients/options'),
  });
}

export function InvoicesPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const clients = useClientOptions();
  const currencies = useCurrencies();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['invoices', page, filters],
    queryFn: () =>
      api.get<Paginated<any> & { summary: any }>('/invoices', { page, pageSize: 25, ...filters }),
  });

  return (
    <>
      <PageHeader
        title={t('invoices.title')}
        action={
          can(P.INVOICES_CREATE) && (
            <Button variant="primary" icon={<Plus className="h-4 w-4" />} onClick={() => navigate('/invoices/new')}>
              {t('invoices.new')}
            </Button>
          )
        }
      />

      {/* §37 — one set of figures, in the base currency: what was paid, at
          the rates the payments were recorded at, and what is still owed, at
          today's rate. The currencies they were raised in are named underneath. */}
      {!!data?.summary?.invoices && (
        <Card className="mb-4">
          <div className="mb-2 flex items-baseline justify-between">
            <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">
              {t('reports.inBaseCurrency', { currency: data.summary.currency })}
            </span>
            <span className="text-caption text-muted">
              {data.summary.invoices} {t('nav.invoices').toLowerCase()}
            </span>
          </div>
          <dl className="divide-y divide-[var(--line)]">
            <Detail label={t('reports.invoiced')}>
              {formatMoney(data.summary.total, data.summary.currency, locale)}
            </Detail>
            <Detail label={t('invoices.paid')}>
              <span className="text-success-fg dark:text-success">{formatMoney(data.summary.paid, data.summary.currency, locale)}</span>
            </Detail>
            <Detail label={t('clients.totalOutstanding')}>
              <strong className={data.summary.outstanding > 0 ? 'text-warning-fg dark:text-warning-dark' : ''}>
                {formatMoney(data.summary.outstanding, data.summary.currency, locale)}
              </strong>
            </Detail>
            {data.summary.billedInCurrencies.length > 1 && (
              <Detail label={t('reports.billedIn')}>
                <span className="font-mono text-mono-sm text-muted">
                  {data.summary.billedInCurrencies.join('  ')}
                </span>
              </Detail>
            )}
          </dl>
          {!!data.summary.unconvertedCurrencies.length && (
            <p className="mt-3 text-caption text-warning-fg dark:text-warning-dark">
              {t('reports.noRateWarning', { currencies: data.summary.unconvertedCurrencies.join(', ') })}
            </p>
          )}
        </Card>
      )}

      <Card className="mb-4" padded={false}>
        <div className="grid gap-3 p-4 sm:grid-cols-2 lg:grid-cols-4">
          <Input
            placeholder={t('common.search')}
            value={filters.q ?? ''}
            onChange={(e) => { setFilters({ ...filters, q: e.target.value }); setPage(1); }}
          />
          <Select
            value={filters.clientId ?? ''}
            onChange={(e) => { setFilters({ ...filters, clientId: e.target.value }); setPage(1); }}
          >
            <option value="">{t('invoices.client')} — {t('common.all')}</option>
            {(clients.data ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {locale === 'ar' && c.companyNameAr ? c.companyNameAr : c.companyName}
              </option>
            ))}
          </Select>
          <Select
            value={filters.currency ?? ''}
            onChange={(e) => { setFilters({ ...filters, currency: e.target.value }); setPage(1); }}
          >
            <option value="">{t('common.currency')} — {t('common.all')}</option>
            {(currencies.data ?? []).map((c) => (
              <option key={c.code} value={c.code}>{c.code}</option>
            ))}
          </Select>
          <Select
            value={filters.status ?? ''}
            onChange={(e) => { setFilters({ ...filters, status: e.target.value }); setPage(1); }}
          >
            <option value="">{t('common.status')} — {t('common.all')}</option>
            {['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'PAID', 'OVERDUE', 'CANCELLED'].map((s) => (
              <option key={s} value={s}>{enumLabel(s, locale)}</option>
            ))}
          </Select>
          <div className="sm:col-span-2 lg:col-span-4">
            <Checkbox
              checked={filters.outstandingOnly === 'true'}
              onChange={(e) => {
                setFilters({ ...filters, outstandingOnly: e.target.checked ? 'true' : '' });
                setPage(1);
              }}
              label={t('invoices.outstandingOnly')}
            />
          </div>
        </div>
      </Card>

      <Card padded={false}>
        {isLoading ? (
          <LoadingBlock />
        ) : error ? (
          <ErrorState error={error} onRetry={() => void refetch()} />
        ) : !data?.items.length ? (
          <EmptyState icon={<FileText className="h-8 w-8" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>{t('tickets.number')}</Th>
                  <Th>{t('invoices.client')}</Th>
                  <Th>{t('invoices.project')}</Th>
                  <Th>{t('common.status')}</Th>
                  <Th>{t('invoices.dueDate')}</Th>
                  <Th align="end">{t('common.total')}</Th>
                  <Th align="end">{t('invoices.remaining')}</Th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((inv) => (
                  <Tr key={inv.id} onClick={() => navigate(`/invoices/${inv.id}`)}>
                    <Td>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="font-mono text-mono-sm text-primary-600 dark:text-primary-400">{inv.number}</span>
                        {inv.contract && (
                          <span title={`${t('contracts.fromContract')} ${inv.contract.number}`} className="text-faint">
                            <Handshake className="h-3.5 w-3.5" aria-hidden />
                            <span className="sr-only">{`${t('contracts.fromContract')} ${inv.contract.number}`}</span>
                          </span>
                        )}
                        {inv.subscription && (
                          <span title={`${t('subs.fromSubscription')} ${inv.subscription.number}`} className="text-faint">
                            <Repeat className="h-3.5 w-3.5" aria-hidden />
                            <span className="sr-only">{`${t('subs.fromSubscription')} ${inv.subscription.number}`}</span>
                          </span>
                        )}
                      </span>
                    </Td>
                    <Td>
                      {locale === 'ar' && inv.client.companyNameAr ? inv.client.companyNameAr : inv.client.companyName}
                    </Td>
                    <Td className="text-muted max-w-xs truncate">
                      {inv.project ? inv.project.code : <span className="text-faint">{t('invoices.noProject')}</span>}
                    </Td>
                    <Td><Badge tone={statusTone(inv.status)}>{enumLabel(inv.status, locale)}</Badge></Td>
                    <Td className="tabular text-muted">{formatDate(inv.dueDate, locale)}</Td>
                    <Td align="end" className="tabular">{formatMoney(inv.totalAmount, inv.currency, locale)}</Td>
                    <Td
                      align="end"
                      className={inv.remainingAmount > 0 ? 'tabular text-warning-fg dark:text-warning-dark' : 'tabular text-success-fg dark:text-success'}
                    >
                      {formatMoney(inv.remainingAmount, inv.currency, locale)}
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={data.page} pageCount={data.pageCount} total={data.total} onChange={setPage} />
          </>
        )}
      </Card>

    </>
  );
}

export function InvoiceDetailPage() {
  const { id = '' } = useParams();
  const { t, locale, localized } = useI18n();
  const { can } = useAuth();
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data: invoice, isLoading, error, refetch } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => api.get<any>(`/invoices/${id}`),
  });

  const issue = useMutation({
    mutationFn: () => api.post(`/invoices/${id}/issue`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['invoice', id] });
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <LoadingBlock />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;

  const paidPercent =
    Number(invoice.totalAmount) > 0 ? (Number(invoice.paidAmount) / Number(invoice.totalAmount)) * 100 : 0;
  const foreign = !!invoice.localCurrency && invoice.currency !== invoice.localCurrency;
  const canTakePayment =
    can(P.PAYMENTS_CREATE) && ['ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status);

  return (
    <>
      <div className="mb-4">
        <Link to="/invoices" className="inline-flex items-center gap-1.5 text-body-sm text-muted hover:text-[var(--text-primary)]">
          <ArrowLeft className="h-4 w-4 flip-rtl" aria-hidden />
          {t('invoices.title')}
        </Link>
      </div>

      <PageHeader
        title={
          <span className="flex flex-wrap items-center gap-3">
            <span className="font-mono text-h2 text-primary-600 dark:text-primary-400">{invoice.number}</span>
            <Link to={`/clients/${invoice.client.id}`} className="hover:underline">
              {locale === 'ar' && invoice.client.companyNameAr
                ? invoice.client.companyNameAr
                : invoice.client.companyName}
            </Link>
          </span>
        }
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            <Badge tone={statusTone(invoice.status)}>{enumLabel(invoice.status, locale)}</Badge>
            <Badge tone="info">{invoice.currency}</Badge>
            {invoice.project && (
              <Link to={`/projects/${invoice.project.id}`}>
                <Badge tone="neutral">{invoice.project.code} — {localized(invoice.project)}</Badge>
              </Link>
            )}
            {invoice.contract && (
              <Link to={`/contracts/${invoice.contract.id}`} title={invoice.contract.title}>
                <Badge tone="plasma">
                  {t('contracts.fromContract')} {invoice.contract.number}
                </Badge>
              </Link>
            )}
            {invoice.subscription && (
              <Link to={`/subscriptions/${invoice.subscription.id}`}>
                <Badge tone="plasma">
                  {t('subs.fromSubscription')} {invoice.subscription.number}
                </Badge>
              </Link>
            )}
            {invoice.subscription && (
              <Link to={`/projects/${invoice.subscription.project.id}`}>
                <Badge tone="neutral">
                  {invoice.subscription.project.code} — {localized(invoice.subscription.project)} · {localized(invoice.subscription.package)}
                </Badge>
              </Link>
            )}
            {invoice.isPartiallyPaid && invoice.status === 'OVERDUE' && (
              <Badge tone="warning">{enumLabel('PARTIALLY_PAID', locale)}</Badge>
            )}
          </span>
        }
        action={
          <div className="flex flex-wrap gap-2">
            <DownloadButton path={`/invoices/${id}/pdf`}>{t('invoices.downloadPdf')}</DownloadButton>
            {/* The one change an issued invoice takes (operator, 2026-10-12). */}
            {can(P.INVOICES_UPDATE) && ['DRAFT', 'ISSUED', 'PARTIALLY_PAID', 'OVERDUE'].includes(invoice.status) && (
              <Button icon={<BadgePercent className="h-4 w-4" />} onClick={() => navigate(`/invoices/${id}/discount`)}>
                {Number(invoice.discountAmount) > 0 ? t('invoices.changeDiscount') : t('invoices.addDiscount')}
              </Button>
            )}
            {invoice.status === 'DRAFT' && can(P.INVOICES_ISSUE) && (
              <Button variant="primary" loading={issue.isPending} onClick={() => issue.mutate()}>
                {t('invoices.issue')}
              </Button>
            )}
            {canTakePayment && invoice.remainingAmount > 0 && (
              <Button variant="primary" onClick={() => navigate(`/invoices/${id}/payments/new`)}>
                {t('invoices.recordPayment')}
              </Button>
            )}
            {can(P.INVOICES_CANCEL) && invoice.status !== 'CANCELLED' && (
              <Button variant="danger" onClick={() => navigate(`/invoices/${id}/cancel`)}>
                {t('invoices.cancel')}
              </Button>
            )}
          </div>
        }
      />

      {invoice.status === 'DRAFT' && (
        <Card className="mb-4 border-warning/40">
          <p className="text-body-sm text-warning-fg dark:text-warning-dark">{t('invoices.draftNotice')}</p>
        </Card>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        {/* min-w-0 and one 0-based track: a wide payments table scrolls inside
            its card instead of pushing the column under the Details panel. */}
        <div className="grid min-w-0 grid-cols-1 gap-5 lg:col-span-2">
          <Card padded={false}>
            <CardHeader title={t('invoices.items')} className="p-5 pb-0" />
            <Table>
              <thead>
                <tr>
                  <Th>{t('invoices.itemDescription')}</Th>
                  <Th align="end">{t('invoices.quantity')}</Th>
                  <Th align="end">{t('invoices.unitPrice')}</Th>
                  <Th align="end">{t('invoices.lineTotal')}</Th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item: any) => (
                  <Tr key={item.id}>
                    <Td>{item.description}</Td>
                    <Td align="end" className="tabular text-muted">{Number(item.quantity)}</Td>
                    <Td align="end" className="tabular text-muted">
                      {formatMoney(item.unitPrice, invoice.currency, locale)}
                    </Td>
                    <Td align="end" className="tabular">{formatMoney(item.lineTotal, invoice.currency, locale)}</Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </Card>

          <Card>
            <CardHeader title={t('invoices.paid')} />
            <div className="mb-2 flex items-baseline justify-between gap-3">
              <span className="text-metric tabular text-[var(--text-primary)]">
                {formatMoney(invoice.paidAmount, invoice.currency, locale)}
              </span>
              <span className="text-body-sm tabular text-muted">
                {t('common.of')} {formatMoney(invoice.totalAmount, invoice.currency, locale)}
              </span>
            </div>
            <ProgressBar percent={paidPercent} tone={paidPercent >= 100 ? 'success' : 'brand'} />

            <div className="mt-5">
              <p className="mb-2 text-caption uppercase text-[var(--text-tertiary)]">{t('payments.title')}</p>
              {invoice.payments?.length ? (
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('payments.number')}</Th>
                      <Th>{t('common.date')}</Th>
                      <Th>{t('payments.method')}</Th>
                      <Th align="end">{t('common.amount')}</Th>
                      <Th align="end">{t('payments.exchangeRate')}</Th>
                      <Th align="end">{t('payments.localAmount')}</Th>
                      <Th align="end">{t('common.actions')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.payments.map((p: any) => (
                      <Tr key={p.id} className={p.reversedAt ? 'opacity-60' : undefined}>
                        <Td>
                          <span className="font-mono text-mono-sm">{p.number}</span>
                          {p.reversedAt && (
                            <Badge tone="error" className="ms-2" title={p.reversalReason ?? undefined}>
                              {t('payments.reversed')}
                            </Badge>
                          )}
                        </Td>
                        <Td className="tabular">{formatDate(p.paymentDate, locale)}</Td>
                        <Td className="text-muted">{p.paymentMethod.replace('_', ' ')}</Td>
                        <Td align="end" className="tabular">{formatMoney(p.amount, p.currency, locale)}</Td>
                        <Td align="end" className="tabular text-muted">{Number(p.exchangeRate)}</Td>
                        <Td align="end" className="tabular text-muted">
                          {formatMoney(p.localAmount, p.localCurrency, locale)}
                        </Td>
                        <Td align="end">
                          <span className="flex justify-end gap-2">
                            {p.attachment && <ReceiptLink file={p.attachment} />}
                            {can(P.PAYMENTS_VIEW) && (
                              <DownloadButton path={`/payments/${p.id}/receipt`}>{t('payments.receipt')}</DownloadButton>
                            )}
                            {!p.reversedAt && can(P.PAYMENTS_REVERSE) && (
                              <Button
                                icon={<Undo2 className="h-4 w-4" />}
                                onClick={() => navigate(`/payments/${p.id}/reverse`)}
                              >
                                {t('payments.reverse')}
                              </Button>
                            )}
                          </span>
                        </Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-body-sm text-muted">{t('common.none')}</p>
              )}
            </div>
          </Card>
        </div>

        <div className="grid gap-5 content-start">
          <Card>
            <CardHeader title={t('tickets.details')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('invoices.issueDate')}>{formatDate(invoice.issueDate, locale)}</Detail>
              <Detail label={t('invoices.dueDate')}>{formatDate(invoice.dueDate, locale)}</Detail>
              {invoice.contract && (
                <Detail label={t('contracts.period')}>
                  {formatDate(invoice.contractPeriodStart, locale)} – {formatDate(invoice.contractPeriodEnd, locale)}
                </Detail>
              )}
              {invoice.subscription && (
                <Detail label={t('subs.detail.period')}>
                  {formatDate(invoice.subscriptionPeriodStart, locale)} – {formatDate(invoice.subscriptionPeriodEnd, locale)}
                </Detail>
              )}
              <Detail label={t('invoices.subtotal')}>{formatMoney(invoice.subtotal, invoice.currency, locale)}</Detail>
              {Number(invoice.discountAmount) > 0 && (
                <Detail label={t('invoices.discount')}>
                  −{formatMoney(invoice.discountAmount, invoice.currency, locale)}
                </Detail>
              )}
              <Detail label={t('invoices.tax')}>{formatMoney(invoice.taxAmount, invoice.currency, locale)}</Detail>
              <Detail label={t('common.total')}>
                <strong>{formatMoney(invoice.totalAmount, invoice.currency, locale)}</strong>
              </Detail>
              <Detail label={t('invoices.remaining')}>
                <strong className={invoice.remainingAmount > 0 ? 'text-warning-fg dark:text-warning-dark' : 'text-success-fg dark:text-success'}>
                  {formatMoney(invoice.remainingAmount, invoice.currency, locale)}
                </strong>
              </Detail>
            </dl>
          </Card>

          {/* §30 — which currency this was raised in, which one the client
              normally bills in, and the money in the base currency. An invoice
              has no rate of its own: what was paid is at each payment's rate
              (the table beside this), what is still owed at today's. */}
          <Card>
            <CardHeader title={t('reports.baseCurrency')} />
            <dl className="divide-y divide-[var(--line)]">
              <Detail label={t('invoices.invoiceCurrency')}>
                <span className="font-mono text-mono-sm">{invoice.currency}</span>
              </Detail>
              <Detail label={t('invoices.clientCurrency')}>
                <span className="font-mono text-mono-sm text-muted">{invoice.client.defaultCurrency}</span>
                {invoice.client.defaultCurrency !== invoice.currency && (
                  <Badge className="ms-2" tone="warning">{t('invoices.currencyOverridden')}</Badge>
                )}
              </Detail>

              <Detail label={t('invoices.collectedLocal')}>
                <strong className="text-success-fg dark:text-success">
                  {formatMoney(invoice.collectedLocalAmount, invoice.localCurrency, locale)}
                </strong>
              </Detail>

              {foreign && invoice.remainingAmount > 0 && invoice.status !== 'CANCELLED' && (
                <Detail label={t('invoices.owedToday')}>
                  {invoice.remainingLocalAmount === null ? (
                    <span className="text-warning-fg dark:text-warning-dark">{t('invoices.noRateToday', { code: invoice.currency })}</span>
                  ) : (
                    <span className="flex flex-col items-end">
                      <span className="whitespace-nowrap tabular">≈ {formatMoney(invoice.remainingLocalAmount, invoice.localCurrency, locale)}</span>
                      <bdi dir="ltr" className="whitespace-nowrap font-mono text-caption text-muted">
                        1 {invoice.currency} = {invoice.currentRate} {invoice.localCurrency}
                      </bdi>
                    </span>
                  )}
                </Detail>
              )}
            </dl>
            {foreign && <p className="mt-3 text-caption text-muted">{t('invoices.ratePerPayment')}</p>}
          </Card>

          {invoice.notes && (
            <Card>
              <CardHeader title={t('common.notes')} />
              <p className="whitespace-pre-wrap text-body-sm">{invoice.notes}</p>
            </Card>
          )}
        </div>
      </div>

    </>
  );
}

export { useClientOptions };
